"use client";

import Link from "next/link";
import { useNavigation } from "@/lib/navigation";
import { useSession } from "@/lib/session";
import { openLogin } from "@/components/auth/login";
import { TAB_PATHS, isProfilePath } from "@/lib/address";
import { BrandMark } from "@/components/shell/BrandMark";
import { TABS } from "@/components/shell/tabs";
import { profilePhotoUrl } from "@/lib/cdn";
import { DesktopBell } from "@/components/notifications/Bell";
import { Skeleton } from "@/components/ui/Spinner";

/**
 * The desktop's top bar: the brand on the left and, on the right, the tabs
 * (tabs.ts) and Perfil as the client's photo and name -- or, signed out, a
 * button to sign in. Os meus eventos stays in view signed out and opens the
 * sign-in sheet; Comunidade opens signed out too. The profile is marked by its address: it is a page
 * over a tab, not a tab of its own. Until the profile answers, a load with a
 * session cookie is drawn signed in, the bell and the photo as placeholders of
 * their size, so nothing moves when they come (the session's likelySignedIn).
 */
export function DesktopHeader() {
  const { tab, pathname } = useNavigation();
  const { client, likelySignedIn } = useSession();
  const onProfile = isProfilePath(pathname);

  const tabClass = (active: boolean) =>
    `relative flex h-16 items-center gap-2 px-4 text-[14px] font-semibold transition-colors ${active ? "text-brand" : "text-ink-soft hover:text-ink"}`;
  const marker = <span className="absolute inset-x-3 bottom-0 h-[3px] rounded-t-full bg-brand" />;

  return (
    <header className="desktop-header sticky top-[var(--strip-h)] z-40 hidden h-16 border-b border-line bg-surface/90 backdrop-blur-md desk:block">
      <div className="mx-auto flex h-full max-w-[1200px] items-center gap-6 px-6">
        <BrandMark size={40} className="min-w-0 shrink" />
        <nav className="flex min-w-0 flex-1 items-center justify-end gap-1" aria-label="Secções">
          {TABS.map(({ id, label, icon: Icon, account }) => {
            const active = tab === id;
            if (account && !likelySignedIn) {
              return (
                <button key={id} type="button" className={tabClass(false)} onClick={() => openLogin()}>
                  <Icon size={17} /> <span className="hidden lg:inline">{label}</span>
                </button>
              );
            }
            return (
              <Link key={id} href={TAB_PATHS[id]} className={tabClass(active)} aria-current={active ? "page" : undefined}>
                <Icon size={17} strokeWidth={active ? 2.4 : 2} /> <span className="hidden lg:inline">{label}</span>
                {active && marker}
              </Link>
            );
          })}
          <DesktopBell />
          {!client && likelySignedIn && (
            <span className={tabClass(false)} aria-hidden>
              <Skeleton className="h-8 w-8 rounded-full" />
              <Skeleton className="hidden h-4 w-20 lg:block" />
            </span>
          )}
          {client && (
            <Link href={TAB_PATHS.profile} className={tabClass(onProfile)} aria-current={onProfile ? "page" : undefined}>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={profilePhotoUrl(client)} alt="" className="h-8 w-8 rounded-full border-2 border-brand-light object-cover" />
              <span className="hidden max-w-[120px] truncate text-ink lg:inline">{client.nickname || client.name}</span>
              {onProfile && marker}
            </Link>
          )}
        </nav>
        {!likelySignedIn && (
          <button
            type="button"
            onClick={() => openLogin()}
            className="pressable shrink-0 rounded-full bg-brand px-4 py-2 text-[13px] font-bold text-white hover:bg-brand-dark"
          >
            <span className="hidden lg:inline">Entrar na minha conta</span><span className="lg:hidden">Entrar</span>
          </button>
        )}
      </div>
    </header>
  );
}
